/**
 * Route-derived breadcrumb generation.
 *
 * The app is mostly flat (sidebar-driven top-level pages), so breadcrumbs are
 * only warranted on the genuinely nested routes: the detail pages under a list,
 * and the upload/preferences pages under their section. This module encodes that
 * hierarchy once; PageHeader calls buildBreadcrumbs() and renders nothing when a
 * route is too shallow to warrant a trail.
 */

export interface Crumb {
  label: string;
  /** Link target for an ancestor. Omitted for the current (last) page. */
  to?: string;
}

/**
 * Navigable ancestor sections, keyed by URL prefix. A page whose path is a
 * child of one of these gets a breadcrumb back to it. The prefixes themselves
 * (the list pages), the dashboard, and the admin pages — which have no landing
 * page to jump back to — get no breadcrumbs.
 */
const ANCESTORS: { prefix: string; label: string }[] = [
  { prefix: '/loan-files', label: 'Loan Files' },
  { prefix: '/parsing/review-queue', label: 'Review Queue' },
  { prefix: '/findings', label: 'Findings' },
  { prefix: '/settings', label: 'Settings' },
];

/** Concise labels for known static leaf routes (override the page title). */
const LEAF_LABELS: Record<string, string> = {
  '/loan-files/new': 'Upload',
  '/settings/notifications': 'Notifications',
};

/**
 * Build the breadcrumb trail for a pathname. `currentLabel` (typically the
 * page's title) names the current page for dynamic routes like /loan-files/:id.
 * Returns [] for shallow routes — render nothing in that case.
 */
export function buildBreadcrumbs(pathname: string, currentLabel?: string): Crumb[] {
  const ancestor = ANCESTORS.find((a) => pathname.startsWith(a.prefix + '/'));
  if (!ancestor) return [];

  const current = LEAF_LABELS[pathname] ?? currentLabel?.trim();
  if (!current) return [];

  return [{ label: ancestor.label, to: ancestor.prefix }, { label: current }];
}
